import { Clock, Copy } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { components } from '@/types/strapi';
import { TwoColumnPopupProps } from './TwoColumnPopup';
interface CountdownTimerProps {
	expiryDate: Date;
}

const CountdownTimer = ({ expiryDate }: CountdownTimerProps) => {
	const [timeLeft, setTimeLeft] = useState({
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0,
	});
	const [isExpired, setIsExpired] = useState(false);

	useEffect(() => {
		const calculateTimeLeft = () => {
			const difference = expiryDate.getTime() - new Date().getTime();

			if (difference <= 0) {
				setIsExpired(true);
				return;
			}

			setTimeLeft({
				days: Math.floor(difference / (1000 * 60 * 60 * 24)),
				hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
				minutes: Math.floor((difference / 1000 / 60) % 60),
				seconds: Math.floor((difference / 1000) % 60),
			});
		};

		calculateTimeLeft();
		const timer = setInterval(calculateTimeLeft, 1000);

		return () => clearInterval(timer);
	}, [expiryDate]);

	if (isExpired) {
		return (
			<div className="flex items-center justify-center">
				<span className="text-lg font-semibold text-red-600">
					Offer has expired!
				</span>
			</div>
		);
	}

	return (
		<div className="grid grid-cols-4 gap-2 text-center">
			<div className="rounded-md bg-white p-2">
				<span className="block text-lg font-bold">
					{String(timeLeft.days).padStart(2, '0')}
				</span>
				<span className="hidden text-xs text-gray-500 lg:block">
					Days
				</span>
				<span className="text-xs text-gray-500 lg:hidden">days</span>
			</div>
			<div className="rounded-md bg-white p-2">
				<span className="block text-lg font-bold">
					{String(timeLeft.hours).padStart(2, '0')}
				</span>
				<span className="hidden text-xs text-gray-500 lg:block">
					Hours
				</span>
				<span className="text-xs text-gray-500 lg:hidden">hrs</span>
			</div>
			<div className="rounded-md bg-white p-2">
				<span className="block text-lg font-bold">
					{String(timeLeft.minutes).padStart(2, '0')}
				</span>
				<span className="hidden text-xs text-gray-500 lg:block">
					Minutes
				</span>
				<span className="text-xs text-gray-500 lg:hidden">min</span>
			</div>
			<div className="rounded-md bg-white p-2">
				<span className="block text-lg font-bold">
					{String(timeLeft.seconds).padStart(2, '0')}
				</span>
				<span className="hidden text-xs text-gray-500 lg:block">
					Seconds
				</span>
				<span className="text-xs text-gray-500 lg:hidden">sec</span>
			</div>
		</div>
	);
};
interface TwoColumnDiscountProps extends Omit<TwoColumnPopupProps, 'content'> {
	content: components['schemas']['PopupTwoColumnImageDiscountOfferComponent'];
}
export const TwoColumnImageDiscountOffer = ({
	content,
}: TwoColumnDiscountProps) => {
	const ctaText = content?.button?.text || 'Shop Now';
	const discountCode = content?.discount_code || '';
	const expiryDate = content?.expiry_date; // Default 24 hours from now
	const handleCopyCode = () => {
		if (discountCode) {
			navigator.clipboard.writeText(discountCode);
		}
	};
	return (
		<div className="exclusive-sale">
			<div className="mt-2 pb-8">
				<div className="mb-5 rounded-md bg-red-50 p-4">
					<div className="flex items-center justify-center">
						<Clock className="mr-2 h-5 w-5 text-red-600" />
						<span className="text-lg font-semibold text-red-600">
							Limited Time Offer!
						</span>
					</div>
					<div className="mt-3 flex justify-center">
						<CountdownTimer
							expiryDate={
								expiryDate
									? new Date(expiryDate)
									: new Date(Date.now() + 24 * 60 * 60 * 1000)
							}
						/>
					</div>
				</div>

				{discountCode && (
					<div className="mb-5 w-full rounded-md border border-dashed border-gray-300 bg-gray-100 p-4 text-center">
						<div className="mb-1 text-xs uppercase text-gray-500">
							Use code
						</div>
						<span className="text-center font-mono text-xl font-bold tracking-wider">
							{discountCode}
							<button
								onClick={handleCopyCode}
								className="transform text-gray-400 hover:text-gray-600"
								title="Copy code">
								<Copy className="ml-2 h-5 w-5" />
							</button>
						</span>
					</div>
				)}
				{content.button?.URL ? (
					<Link href={content.button.URL}>
						<Button
							className="w-full py-6 text-base"
							variant="primary"
							size="lg">
							{ctaText}
						</Button>
					</Link>
				) : (
					<Button
						className="w-full py-6 text-base"
						variant="primary"
						size="lg">
						{ctaText}
					</Button>
				)}
			</div>
		</div>
	);
};
